import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "../config/routes";
import PrivacyPolicyDrawer from "@features/home/components/PrivacyPolicyDrawer";

type PrivacyPolicyContextValue = {
  isOpen: boolean;
  openPrivacyPolicy: () => void;
  closePrivacyPolicy: () => void;
};

const PrivacyPolicyContext = createContext<PrivacyPolicyContextValue | null>(null);

export const PrivacyPolicyProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const openPrivacyPolicy = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closePrivacyPolicy = useCallback(() => {
    setIsOpen(false);
    if (location.pathname === ROUTES.POLITICAS_PRIVACIDAD) {
      navigate(ROUTES.HOME, { replace: true });
    }
  }, [location.pathname, navigate]);

  const value = useMemo(
    () => ({
      isOpen,
      openPrivacyPolicy,
      closePrivacyPolicy,
    }),
    [isOpen, openPrivacyPolicy, closePrivacyPolicy],
  );

  return (
    <PrivacyPolicyContext.Provider value={value}>
      {children}
      <PrivacyPolicyDrawer isOpen={isOpen} onClose={closePrivacyPolicy} />
    </PrivacyPolicyContext.Provider>
  );
};

export const usePrivacyPolicy = () => {
  const context = useContext(PrivacyPolicyContext);
  if (!context) {
    throw new Error("usePrivacyPolicy debe usarse dentro de PrivacyPolicyProvider");
  }
  return context;
};
